// Client side of the Daily leaderboard: an anonymous device id, a future-only
// opt-out, and the two fetch calls to the Worker. Kept out of app.ts so the
// networking + localStorage plumbing lives in one place (mirrors how share.ts
// owns the sharing concern). Every call degrades to a no-op/null when the route
// isn't reachable (e.g. `vite` dev, where the Worker doesn't run) or storage is
// blocked (private mode) — the game never depends on the leaderboard.
import type { Decision } from "../types";

const CID_KEY = "gpdl_cid"; // anonymous, per-device id (not an account)
const OPTOUT_KEY = "gpdl_lb_optout"; // "1" => never auto-submit
const NOTICE_KEY = "gpdl_lb_notice"; // "1" => one-time privacy notice shown

function makeId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `c-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/** A stable anonymous id for this device, minted once and reused. Lets the
 *  server tie a player's repeated submissions together and the UI highlight
 *  their own row — without any login. */
export function getClientId(): string {
  try {
    let id = localStorage.getItem(CID_KEY);
    if (!id) {
      id = makeId();
      localStorage.setItem(CID_KEY, id);
    }
    return id;
  } catch {
    return makeId(); // ephemeral, but submission still works for this session
  }
}

export function isOptedOut(): boolean {
  try {
    return localStorage.getItem(OPTOUT_KEY) === "1";
  } catch {
    return false;
  }
}

export function setOptedOut(v: boolean): void {
  try {
    if (v) localStorage.setItem(OPTOUT_KEY, "1");
    else localStorage.removeItem(OPTOUT_KEY);
  } catch {
    /* non-fatal */
  }
}

export function leaderboardNoticeSeen(): boolean {
  try {
    return localStorage.getItem(NOTICE_KEY) === "1";
  } catch {
    return true; // can't record it → don't nag on every result
  }
}

export function markLeaderboardNoticeSeen(): void {
  try {
    localStorage.setItem(NOTICE_KEY, "1");
  } catch {
    /* non-fatal */
  }
}

export interface SubmitPayload {
  readonly day: string;
  readonly clientId: string;
  readonly name: string;
  readonly chaos: boolean;
  readonly decisionLog: readonly Decision[];
}

/** Post a finished daily to the leaderboard. Returns whether it was accepted;
 *  any failure (offline, route missing, rejected) resolves false, never throws. */
export async function submitScore(p: SubmitPayload): Promise<boolean> {
  try {
    const res = await fetch("/api/score", {
      method: "POST",
      headers: { "content-type": "application/json" },
      // company: honeypot, always empty from a real client (mirrors /api/suggest).
      body: JSON.stringify({ ...p, company: "" }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export interface LeaderboardEntry {
  readonly name: string;
  readonly teamPts: number;
  readonly driverPts: number;
  readonly wccPos: number;
  readonly you?: boolean;
}

export interface LeaderboardResponse {
  readonly day: string;
  readonly entries: readonly LeaderboardEntry[];
  // The caller's own rank + score for the day, even when they're outside the
  // returned top slice. Absent if they haven't submitted that day.
  readonly youRank?: number;
  readonly you?: { readonly teamPts: number; readonly driverPts: number; readonly wccPos: number };
}

/** Fetch the day's top entries. `clientId` lets the server flag the caller's own
 *  row and report their rank. Returns null on any error. */
export async function fetchLeaderboard(
  day: string,
  clientId: string,
): Promise<LeaderboardResponse | null> {
  try {
    const qs = `day=${encodeURIComponent(day)}&cid=${encodeURIComponent(clientId)}`;
    const res = await fetch(`/api/leaderboard?${qs}`);
    if (!res.ok) return null;
    return (await res.json()) as LeaderboardResponse;
  } catch {
    return null;
  }
}
